/* eslint-disable no-unused-vars */
import React, { useEffect, useMemo, useRef } from 'react'
import * as yup from 'yup'

import { CustomersCustomerTypeChoices } from '@/apollo/graphql'
import {
  Form,
  FormButtons,
  OptionSelector,
  TextInput,
  TextArea,
} from '@/components'
import { useForm } from '@/hooks/index'
import { t } from '@/i18n'
import UserSelector from '@/pages/User/userSelector'
import { ICustomer, IOption } from '@/types'

interface Props {
  isEditing?: boolean
  initialData?: Partial<ICustomer>
  loading: boolean
  submitting: boolean
  onCancel: () => void
  onSubmit: (d: ICustomer) => void
}

const CustomerForm: React.FC<Props> = ({
  isEditing,
  initialData,
  loading,
  submitting,
  onCancel,
  onSubmit,
}) => {
  const schema = useMemo(
    () =>
      yup
        .object({
          name: yup.string().required(t('required_field_error')),
          type: yup
            .mixed<CustomersCustomerTypeChoices>()
            .oneOf(
              Object.values(CustomersCustomerTypeChoices),
              t('required_field_error'),
            ),
        })
        .required(),
    [],
  )

  const formRef = useRef<HTMLFormElement | null>(null)
  const { control, isValid, onFormSubmit, onTriggerChange } = useForm({
    isEditing,
    defaultValues: initialData,
    schema,
    onSubmit,
  })

  const typeOptions: IOption<CustomersCustomerTypeChoices>[] = useMemo(
    () =>
      Object.entries(CustomersCustomerTypeChoices).map(([k, v]) => ({
        label: t(k),
        value: v,
      })),
    [],
  )

  useEffect(() => {
    // @ts-ignore
    onTriggerChange('accountManager', initialData?.accountManager?.id)
  }, [initialData?.accountManager?.id, onTriggerChange])

  const emptyValue = useMemo(() => (isEditing ? '' : '...'), [isEditing])

  return (
    <div className="edit-info">
      {loading ? (
        <div className="data-box">
          <div className="loading-text">{t('loading')}</div>
        </div>
      ) : (
        <>
          <Form<ICustomer>
            className={isEditing ? 'editing' : 'view'}
            isEditing={isEditing}
            formRef={formRef}
            control={control}
            onSubmit={onFormSubmit}
          >
            <div className="form-fields">
              <TextInput
                name="name"
                isEditing={isEditing}
                className="w-33"
                control={control}
                emptyValue={emptyValue}
                disabled={!Boolean(isEditing)}
                label={t('name')}
              />
              <TextInput
                isEditing={isEditing}
                name="email"
                type="email"
                className="w-33"
                control={control}
                emptyValue={emptyValue}
                disabled={!Boolean(isEditing)}
                label={t('email')}
              />
              <TextInput
                isEditing={isEditing}
                name="phone"
                className="w-33"
                control={control}
                emptyValue={emptyValue}
                disabled={!Boolean(isEditing)}
                label={t('phone')}
              />
              <TextInput
                isEditing={isEditing}
                className="w-33"
                control={control}
                name="additionalPhone"
                emptyValue={emptyValue}
                disabled={!Boolean(isEditing)}
                label={t('additional_phone')}
              />
              <TextInput
                isEditing={isEditing}
                name="fax"
                className="w-33"
                control={control}
                emptyValue={emptyValue}
                disabled={!Boolean(isEditing)}
                label={t('fax')}
              />
              <TextInput
                isEditing={isEditing}
                control={control}
                name="additionalFax"
                emptyValue={emptyValue}
                disabled={!Boolean(isEditing)}
                label={t('additional_fax')}
              />
              <TextInput
                isEditing={isEditing}
                className="w-33"
                control={control}
                name="address"
                emptyValue={emptyValue}
                disabled={!Boolean(isEditing)}
                label={t('address')}
              />
              <TextInput
                isEditing={isEditing}
                className="w-33"
                control={control}
                name="additionalAddress"
                emptyValue={emptyValue}
                disabled={!Boolean(isEditing)}
                label={t('additional_address')}
              />
              <TextInput
                isEditing={isEditing}
                name="city"
                className="w-33"
                control={control}
                emptyValue={emptyValue}
                disabled={!Boolean(isEditing)}
                label={t('city')}
              />
              <TextInput
                isEditing={isEditing}
                control={control}
                name="country"
                className="w-33"
                emptyValue={emptyValue}
                disabled={!Boolean(isEditing)}
                label={t('country')}
                defaultValue={'ישראל'}
              />
              <TextInput
                isEditing={isEditing}
                control={control}
                name="zip"
                className="w-33"
                type="number"
                emptyValue={emptyValue}
                disabled={!Boolean(isEditing)}
                label={t('zip')}
              />
              <TextInput
                isEditing={isEditing}
                className="w-33"
                control={control}
                name="bankAccountNumber"
                emptyValue={emptyValue}
                disabled={!Boolean(isEditing)}
                label={t('bank_account_number')}
              />
              <TextInput
                isEditing={isEditing}
                className="w-33"
                control={control}
                name="branchNumber"
                emptyValue={emptyValue}
                disabled={!Boolean(isEditing)}
                label={t('branch_number')}
              />
              <TextInput
                isEditing={isEditing}
                className="w-33"
                control={control}
                name="contactName"
                emptyValue={emptyValue}
                disabled={!Boolean(isEditing)}
                label={t('contact_name')}
              />
              <TextInput
                isEditing={isEditing}
                className="w-33"
                control={control}
                name="companyNumber"
                emptyValue={emptyValue}
                disabled={!Boolean(isEditing)}
                label={t('company_number')}
              />
              <OptionSelector
                required
                isEditing={isEditing}
                name="type"
                className="w-33"
                control={control}
                emptyValue={emptyValue}
                disabled={!Boolean(isEditing)}
                options={typeOptions}
                label={t('type')}
              />
              <UserSelector
                isFormElement
                control={control}
                name="accountManager"
                label={t('accountManager')}
                isEditing={isEditing}
                initial={initialData?.accountManager}
              />
              <TextInput
                isEditing={isEditing}
                name="shouldUnionInvoices"
                type="checkbox"
                control={control}
                emptyValue={emptyValue}
                disabled={!Boolean(isEditing)}
                label={t('should_union_invoices')}
              />
              <TextInput
                isEditing={isEditing}
                name="standingOrderLimit"
                type="number"
                className="w-33"
                control={control}
                isCurrency
                emptyValue={emptyValue}
                disabled={!Boolean(isEditing)}
                label={t('standing_order_limit')}
                min={0}
              />
              <TextInput
                name="isActive"
                type="checkbox"
                control={control}
                default={true}
                emptyValue={emptyValue}
                disabled={!Boolean(isEditing)}
                label={t('isActive')}
              />
              <TextArea
                isEditing={isEditing}
                className="w-50"
                control={control}
                name="notes"
                label={t('notes')}
                rows={3}
                emptyValue={emptyValue}
                disabled={!Boolean(isEditing)}
              />
            </div>
          </Form>
          <FormButtons
            visible={isEditing}
            formRef={formRef}
            isFormValid={isValid}
            submitting={submitting}
            onCancel={onCancel}
          />
        </>
      )}
    </div>
  )
}

export default CustomerForm
